// SalonClosureModal.jsx - Mark full days when the salon is closed (no bookings).
// Web port of UlloorStyles@e7a5f9b:src/components/SalonClosureModal.js. Saving a newly-added
// closure date sends a reschedule request to any confirmed bookings on that date, same as
// mobile — see staffConfigService.saveSalonClosures.
import React, { useEffect, useState } from 'react';
import '../pages/salon/SalonStaffManagement.css';

const DAYS_AHEAD = 30;

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const generateDates = () => {
  const dates = [];
  const today = new Date();
  for (let i = 0; i < DAYS_AHEAD; i += 1) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    dates.push(date);
  }
  return dates;
};

function SalonClosureModal({ isOpen, onClose, onSave, existingClosures = {}, isSaving = false }) {
  const [closures, setClosures] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setClosures(existingClosures || {});
      setHasChanges(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const toggleDate = (dateStr) => {
    setClosures((prev) => {
      const next = { ...prev };
      if (next[dateStr]) {
        delete next[dateStr];
      } else {
        next[dateStr] = { date: dateStr };
      }
      return next;
    });
    setHasChanges(true);
  };

  const handleSave = () => {
    onSave(closures);
  };

  const count = Object.keys(closures).length;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content scheduling-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3>Salon Closed Days</h3>
            <p className="modal-subtitle">Select dates when the salon is fully closed. Customers cannot book on those days.</p>
            <p className="modal-warning">
              Marking a new date as closed will send a reschedule request to any bookings already
              confirmed for that day — their slot stays held until the customer picks a new time.
            </p>
            {count > 0 && <div className="leave-summary-badge closure-summary-badge">{count} day{count !== 1 ? 's' : ''} marked closed</div>}
          </div>
          <button onClick={onClose} className="modal-close-btn">✕</button>
        </div>

        <div className="modal-body scrollable-body">
          <div className="date-grid">
            {generateDates().map((date) => {
              const dateStr = formatDate(date);
              const isClosed = !!closures[dateStr];
              const isToday = formatDate(new Date()) === dateStr;
              const isSunday = date.getDay() === 0;

              return (
                <button
                  type="button"
                  key={dateStr}
                  className={[
                    'date-cell',
                    isClosed && 'date-cell-closed',
                    isToday && 'date-cell-today',
                    isSunday && !isClosed && 'date-cell-sunday',
                  ].filter(Boolean).join(' ')}
                  onClick={() => toggleDate(dateStr)}
                >
                  {isToday && <span className="date-cell-today-badge">Today</span>}
                  <span className="date-cell-day">{date.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                  <span className="date-cell-number">{date.getDate()}</span>
                  <span className="date-cell-month">{date.toLocaleDateString('en-US', { month: 'short' })}</span>
                  {isClosed && <span className="date-cell-closed-badge">Closed</span>}
                </button>
              );
            })}
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="cancel-modal-btn">Cancel</button>
          <button onClick={handleSave} className="save-modal-btn" disabled={isSaving || !hasChanges}>
            {isSaving ? 'Saving...' : hasChanges ? 'Save' : 'No changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SalonClosureModal;
